import { User } from '@auth0/auth0-react';

export enum ProviderAccess {
  ADMIN_ACCESS_APP = 'admin:access:app',
  MAUD_ACCESS_APP = 'maud:access:app',
  OPS_ACCESS_APP = 'ops:access:app',
}

export const providerDomainMap: Record<string, ProviderAccess> = {
  admin: ProviderAccess.ADMIN_ACCESS_APP,
  maud: ProviderAccess.MAUD_ACCESS_APP,
  ops: ProviderAccess.OPS_ACCESS_APP,
};

export const defaultProviderAccess: AccessMap<ProviderAccess> = {
  [ProviderAccess.ADMIN_ACCESS_APP]: false,
  [ProviderAccess.MAUD_ACCESS_APP]: false,
  [ProviderAccess.OPS_ACCESS_APP]: false,
};

export enum MaudPermissions {
  MAUD_PATIENT_SEARCH = 'maud:permission:patient-search',
  MAUD_ADD_PATIENT = 'maud:permission:add-patient',
  MAUD_PATIENT_DASHBOARD = 'maud:permission:patient-dashboard',
  MAUD_ASSESSMENTS = 'maud:permission:assessments',
  MAUD_PROVIDER_PANEL = 'maud:permission:provider-panel',
  MAUD_CARE_TEAM_ASSIGNMENTS = 'maud:permission:care-team-assignments',
  MAUD_EDIT_FAMILY_TASK = 'maud:permission:edit-family-task',
  MAUD_APPOINTMENTS = 'maud:permission:appointments',
  MAUD_CHATS = 'maud:permission:chats',
  MAUD_CARE_PLAN = 'maud:permission:care-plan',
  MAUD_AVAILABILITY = 'maud:permission:availability',
  MAUD_SCHEDULE_PROXY = 'maud:permission:schedule-proxy',
  MAUD_PROVIDER_REASSIGNMENTS = 'write:provider-reassignments',
  MAUD_REORDER_EQUIP_SCALE = 'maud:permission:reorder-equip-scale',
}

export const ROLE_WITH_EDIT_FAMILY_TASK_PERMISSION = [
  'Admissions Operations',
  'Admissions Specialist',
  'Admissions Supervisor',
  'Care Coordinator',
  'Family Experience Navigator',
  'Practice Coordinator',
  'Recovery Navigator',
  'Care Navigator',
];

export const MaudPermissionsMap: Record<MaudPermissions, Permission> = {
  [MaudPermissions.MAUD_PATIENT_SEARCH]: {
    display: 'Patient search',
    index: 1,
  },
  [MaudPermissions.MAUD_ADD_PATIENT]: {
    addendum: '(for admission team only)',
    display: 'Add a patient',
    index: 2,
  },
  [MaudPermissions.MAUD_PATIENT_DASHBOARD]: {
    display: 'Patient dashboard',
    index: 3,
  },
  [MaudPermissions.MAUD_ASSESSMENTS]: {
    display: 'Assessments',
  },
  [MaudPermissions.MAUD_PROVIDER_PANEL]: {
    addendum: '(for admission team only)',
    display: 'Provider panel',
  },
  [MaudPermissions.MAUD_CARE_TEAM_ASSIGNMENTS]: {
    addendum: '(for admission team only)',
    display: 'Provider team assignments',
  },
  [MaudPermissions.MAUD_EDIT_FAMILY_TASK]: {
    display: 'Edit family task',
  },
  [MaudPermissions.MAUD_APPOINTMENTS]: {
    display: 'Appointments',
  },
  [MaudPermissions.MAUD_CHATS]: {
    display: 'Chats',
  },
  [MaudPermissions.MAUD_CARE_PLAN]: {
    addendum: '(for admission team only)',
    display: 'Care plan',
    index: 4,
  },
  [MaudPermissions.MAUD_AVAILABILITY]: {
    display: 'Availability (self)',
    index: 5,
  },
  [MaudPermissions.MAUD_SCHEDULE_PROXY]: {
    display: 'Scheduling admin',
    index: 6,
  },
  [MaudPermissions.MAUD_PROVIDER_REASSIGNMENTS]: {
    display: 'Provider reassignments',
    index: 7,
  },
  [MaudPermissions.MAUD_REORDER_EQUIP_SCALE]: {
    display: 'Reorder Equip scale',
    index: 8,
  },
};

export enum MaudRoles {
  MAUD_ADVANCED_ACCESS = 'maud:role:advanced-access',
  MAUD_GENERAL_ACCESS = 'maud:role:general-access',
}

export const MaudRolesMap: Record<MaudRoles, Role<MaudPermissions>> = {
  [MaudRoles.MAUD_ADVANCED_ACCESS]: {
    description:
      'Select advanced access checkbox to select all permissions listed below or customize by selecting applicable modules only.',
    display: 'Advanced access',
    permissions: MaudPermissionsMap,
  },
  [MaudRoles.MAUD_GENERAL_ACCESS]: {
    description:
      'This will allow basic access for Maud application with a read only view of all patient related information.',
    display: 'General access',
  },
};

export const defaultMaudRolesState: Record<
  MaudRoles,
  RolesState<MaudPermissions>
> = {
  [MaudRoles.MAUD_GENERAL_ACCESS]: {
    ...MaudRolesMap[MaudRoles.MAUD_GENERAL_ACCESS],
    isChecked: false,
    isDisabled: true,
    permissions: {
      ...((): Record<MaudPermissions, PermissionsState> => {
        const permissionsState: Partial<
          Record<MaudPermissions, PermissionsState>
        > = {};
        Object.entries(
          MaudRolesMap[MaudRoles.MAUD_GENERAL_ACCESS].permissions ?? {},
        ).forEach(([permission, value]: [MaudPermissions, Permission]) => {
          permissionsState[permission] = {
            ...value,
            isChecked: false,
          };
        });

        return permissionsState as Record<MaudPermissions, PermissionsState>;
      })(),
    },
  },
  [MaudRoles.MAUD_ADVANCED_ACCESS]: {
    ...MaudRolesMap[MaudRoles.MAUD_ADVANCED_ACCESS],
    isChecked: false,
    permissions: {
      ...((): Record<MaudPermissions, PermissionsState> => {
        const permissionsState: Partial<
          Record<MaudPermissions, PermissionsState>
        > = {};
        Object.entries(
          MaudRolesMap[MaudRoles.MAUD_ADVANCED_ACCESS].permissions ?? {},
        ).forEach(([permission, value]: [MaudPermissions, Permission]) => {
          permissionsState[permission] = {
            ...value,
            isChecked: false,
            isHidden: [
              MaudPermissions.MAUD_ASSESSMENTS,
              MaudPermissions.MAUD_PROVIDER_PANEL,
              MaudPermissions.MAUD_CARE_TEAM_ASSIGNMENTS,
              MaudPermissions.MAUD_APPOINTMENTS,
              MaudPermissions.MAUD_CHATS,
              MaudPermissions.MAUD_EDIT_FAMILY_TASK,
            ].includes(permission),
          };
        });

        return permissionsState as Record<MaudPermissions, PermissionsState>;
      })(),
    },
  },
};

export enum OpsPermissions {
  OPS_SCRIPTSURE_SYNC_DASHBOARD = 'ops:permission:scriptsure-sync-dashboard',
}

export const OpsPermissionsMap: Record<OpsPermissions, Permission> = {
  [OpsPermissions.OPS_SCRIPTSURE_SYNC_DASHBOARD]: {
    display: 'ScriptSure sync dashboard',
  },
};

export enum OpsRoles {
  // UI-only grouping that renders the permission checkboxes; excluded from the saved roles
  OPS_PERMISSIONS = 'ops:role:permissions',
  // Hidden role, checked whenever Ops access is on
  OPS_GENERAL_ACCESS = 'ops:role:general-access',
}

export const OpsRolesMap: Record<OpsRoles, Role<OpsPermissions>> = {
  [OpsRoles.OPS_PERMISSIONS]: {
    description: '',
    display: 'Ops permissions',
    permissions: OpsPermissionsMap,
  },
  [OpsRoles.OPS_GENERAL_ACCESS]: {
    description: '',
    display: 'General access',
  },
};

export const defaultOpsRolesState: Record<
  OpsRoles,
  RolesState<OpsPermissions>
> = {
  [OpsRoles.OPS_GENERAL_ACCESS]: {
    ...OpsRolesMap[OpsRoles.OPS_GENERAL_ACCESS],
    isChecked: false,
    isHidden: true,
    permissions: {} as Record<OpsPermissions, PermissionsState>,
  },
  [OpsRoles.OPS_PERMISSIONS]: {
    ...OpsRolesMap[OpsRoles.OPS_PERMISSIONS],
    isCheckboxHidden: true,
    isChecked: false,
    permissions: Object.fromEntries(
      Object.entries(OpsPermissionsMap).map(
        ([permission, value]: [OpsPermissions, Permission]) => [
          permission,
          { ...value, isChecked: false },
        ],
      ),
    ) as Record<OpsPermissions, PermissionsState>,
  },
};

export const UI_ONLY_ROLES: string[] = [
  MaudRoles.MAUD_ADVANCED_ACCESS,
  OpsRoles.OPS_PERMISSIONS,
];

// TODO: these are empty intentionally for the sake of having the frontend render data without
// backend integration, but once that's in place this entire file should be removable.

export enum AdminRoles {
  ADMIN_MANAGE_USERS = 'admin:role:manage-users',
  ADMIN_MANAGE_CONTENT = 'admin:role:manage-content',
}

export const AdminRolesMap: Record<AdminRoles, Role<never>> = {
  [AdminRoles.ADMIN_MANAGE_USERS]: {
    description: '',
    display: 'Manage users',
  },
  [AdminRoles.ADMIN_MANAGE_CONTENT]: {
    description: '',
    display: 'Manage content',
  },
};

export const defaultAdminRolesState: Record<AdminRoles, RolesState<never>> = {
  [AdminRoles.ADMIN_MANAGE_USERS]: {
    ...AdminRolesMap[AdminRoles.ADMIN_MANAGE_USERS],
    isChecked: false,
    permissions: {},
  },
  [AdminRoles.ADMIN_MANAGE_CONTENT]: {
    ...AdminRolesMap[AdminRoles.ADMIN_MANAGE_CONTENT],
    isChecked: false,
    isHidden: true,
    permissions: {},
  },
};

export type ProviderRole = AdminRoles | MaudRoles | OpsRoles;

export const AUTH0_USER_PERMISSIONS_KEY =
  'https://app.equip.health/permissions';
export const AUTH0_USER_ROLES_KEY = 'https://app.equip.health/roles';
export const AUTH0_JWT_USER_EXTERNAL_ID_CLAIM_KEY =
  'https://app.equip.health/external_user_id';

export type Auth0User = {
  [AUTH0_USER_PERMISSIONS_KEY]: string[];
  [AUTH0_USER_ROLES_KEY]: string[];
} & User;

export type Auth0AccessToken = {
  [AUTH0_JWT_USER_EXTERNAL_ID_CLAIM_KEY]: string;
};
